<template>
  <section id="join-test" class="lm-section lm-tint">
    <div class="container-wrapper">
      <div class="lm-split">
        <div>
          <p class="lm-eyebrow">BECOME A TESTER · {{ minimumAge }}+</p>
          <h2>Help shape LODOMELIX.</h2>
          <p class="lm-lead">
            Explore the heroes. Try new builds. Tell us what works—and what gets
            in your way.
          </p>
          <p>
            Try a development build, report bugs and share honest feedback. It
            may contain bugs, and access depends on the active Google Play
            testing track.
          </p>
          <v-btn
            color="primary"
            rounded
            depressed
            large
            href="#test-registration"
            >Join the test</v-btn
          >
        </div>
        <div class="lm-card">
          <h3>Your perspective matters.</h3>
          <ol class="lm-test-steps">
            <li>Register with the Google Account you use in Google Play.</li>
            <li>Verify your email, then wait for access confirmation.</li>
            <li>
              Open the supplied Play link with that same account and accept
              participation yourself.
            </li>
          </ol>
          <p class="lm-note">
            Participation is voluntary. No payment or rewards are guaranteed.
            Registration alone does not grant access or complete Google Play
            opt-in.
          </p>
          <a href="mailto:lodomelix@ari.company"
            >Send honest feedback to lodomelix@ari.company</a
          >
        </div>
      </div>
      <div
        id="test-registration"
        class="lm-form-grid lm-enrollment"
        aria-labelledby="lm-registration-title"
      >
        <form class="lm-card" @submit.prevent="register">
          <h3 id="lm-registration-title">Join the initial test</h3>
          <p>
            Adults aged {{ minimumAge }} or over only. Your age is
            self-declared; email verification does not verify your age.
          </p>
          <p v-if="!available" class="lm-draft" role="status">
            {{
              loading
                ? "Checking enrollment availability…"
                : "Enrollment is not open yet. Registration remains disabled while the testing notices and operational requirements are under review."
            }}
          </p>
          <label for="lm-test-email"
            >Google Account email used in Google Play *</label
          ><input
            id="lm-test-email"
            v-model.trim="form.email"
            class="lm-input"
            type="email"
            autocomplete="email"
            maxlength="254"
            required
            aria-describedby="lm-email-help"
          />
          <p id="lm-email-help" class="lm-muted">
            Any valid Google Account email—not just a gmail.com address.
          </p>
          <label for="lm-test-age">Age in completed years *</label
          ><input
            id="lm-test-age"
            v-model.number="form.age"
            class="lm-input"
            type="number"
            inputmode="numeric"
            :min="minimumAge"
            max="120"
            step="1"
            required
            @input="ageMessage = ''"
          />
          <p
            v-if="ageMessage"
            class="lm-feedback lm-feedback--error"
            role="alert"
          >
            {{ ageMessage }}
          </p>
          <label for="lm-test-name"
            >Display name <span class="lm-muted">(optional)</span></label
          ><input
            id="lm-test-name"
            v-model.trim="form.display_name"
            class="lm-input"
            type="text"
            autocomplete="nickname"
            maxlength="80"
          />
          <label for="lm-test-device"
            >Android device model / version
            <span class="lm-muted">(optional)</span></label
          ><input
            id="lm-test-device"
            v-model.trim="form.device"
            class="lm-input"
            type="text"
            maxlength="160"
            aria-describedby="lm-device-help"
          />
          <p id="lm-device-help" class="lm-muted">
            Helps us understand device-specific bugs. Do not include serial
            numbers or device IDs.
          </p>
          <label class="lm-checkbox"
            ><input
              v-model="form.age_confirmed"
              type="checkbox"
              required
            /><span
              >I confirm that I am {{ minimumAge }} or older and that the age I
              provided is accurate.</span
            ></label
          >
          <label class="lm-checkbox" v-if="reviewDrafts"
            ><input
              v-model="form.terms_accepted"
              type="checkbox"
              required
            /><span
              >I agree to the
              <router-link to="/games/ludomelix/testing-terms" target="_blank"
                >Testing Terms (new tab)</router-link
              >
              and acknowledge the
              <router-link
                to="/games/ludomelix/privacy-policy#tester-registration"
                target="_blank"
                >Privacy Policy (new tab)</router-link
              >.</span
            ></label
          >
          <template v-if="community"
            ><label class="lm-checkbox"
              ><input v-model="form.community_requested" type="checkbox" /><span
                >Send me an invitation to the LODOMELIX feedback community on
                {{ community.name }}.</span
              ></label
            >
            <p class="lm-muted">
              {{ community.visibility }} Joining is voluntary and separate from
              testing access.
            </p></template
          >
          <p v-else class="lm-muted">
            No official discussion community is connected yet. Testing does not
            require joining a community.
          </p>
          <p class="lm-muted">
            Where enabled, a private technical Google Group grants Play
            eligibility. It has no conversation delivery and is separate from a
            discussion community. Google and ARI’s access administrators can
            process your membership email.
          </p>
          <p class="lm-note">
            <strong>No guaranteed reward.</strong> Participation alone does not
            entitle you to payment, employment, ownership, revenue or in-game
            rewards. Your feedback can be positive or critical.
          </p>
          <div class="lm-honeypot" aria-hidden="true">
            <label
              >Website<input
                v-model="form.website"
                type="text"
                tabindex="-1"
                autocomplete="off"
            /></label>
          </div>
          <v-btn
            type="submit"
            color="primary"
            rounded
            depressed
            :disabled="!available || busy"
            >{{ busy ? "Please wait…" : "Send verification code" }}</v-btn
          >
          <p
            v-if="requestMessage"
            class="lm-feedback"
            :class="{ 'lm-feedback--error': requestError }"
            :role="requestError ? 'alert' : 'status'"
          >
            {{ requestMessage }}
          </p>
        </form>
        <div class="lm-card">
          <h3>Verify &amp; manage your registration</h3>
          <p>
            Enter the code from your email within 30 minutes. It works once. If
            it expires, submit the registration form again to receive a new one.
          </p>
          <form @submit.prevent="verify">
            <label for="lm-test-code">Email verification code</label
            ><input
              id="lm-test-code"
              v-model.trim="code"
              class="lm-input"
              type="text"
              autocomplete="one-time-code"
              minlength="32"
              maxlength="32"
              required
              spellcheck="false"
              autocapitalize="off"
            /><v-btn
              color="primary"
              outlined
              rounded
              type="submit"
              :disabled="!available || busy"
              >Verify email</v-btn
            >
          </form>
          <div v-if="state" class="lm-test-state" role="status">
            <p class="lm-eyebrow">{{ stateTitle }}</p>
            <p>{{ stateMessage }}</p>
            <template v-if="state === 'granted'"
              ><a
                :href="optIn"
                target="_blank"
                rel="noopener noreferrer"
                class="lm-play-link"
                >Open Google Play opt-in ↗</a
              >
              <p class="lm-note">
                Use {{ verifiedEmail || "the email address you verified" }} in
                Google Play. Choose to participate there. Allow time for access
                to appear; if it does not, check the account and contact
                support. We cannot tell whether you opted in, installed or
                played.
              </p>
              <p
                v-if="
                  emailNotice === 'delivery_unconfirmed' ||
                  emailNotice === 'sending'
                "
                class="lm-muted"
              >
                Instruction-email delivery is unconfirmed. Use the link above;
                we will not send duplicate invitations automatically.
              </p></template
            >
            <p v-if="state === 'withdrawn' && removalPending">
              Access-group removal still needs operator review. Email
              <a href="mailto:lodomelix@ari.company">lodomelix@ari.company</a>.
              An existing membership is not removed automatically.
            </p>
            <p v-if="state === 'withdrawn'">
              To leave Google Play testing, use its opt-in page with the same
              account. To delete your game account, use the separate
              <router-link to="/games/ludomelix/delete-account"
                >account-deletion flow</router-link
              >.
            </p>
          </div>
          <div v-if="authenticated && state !== 'withdrawn'" class="lm-actions">
            <v-btn
              v-if="state !== 'granted'"
              rounded
              outlined
              :disabled="busy"
              @click="provision"
              >Retry access check</v-btn
            ><v-btn text :disabled="busy" @click="confirmWithdraw = true"
              >Withdraw registration</v-btn
            >
          </div>
          <div v-if="confirmWithdraw" class="lm-feedback">
            <p>
              Withdraw this testing registration? This stops further
              provisioning. Membership created by this flow will be removed
              where possible. Existing or uncertain memberships require review.
            </p>
            <v-btn outlined rounded :disabled="busy" @click="withdraw"
              >Confirm withdrawal</v-btn
            ><v-btn text @click="confirmWithdraw = false"
              >Keep registration</v-btn
            >
          </div>
          <p
            v-if="manageError"
            class="lm-feedback lm-feedback--error"
            role="alert"
          >
            {{ manageError }}
          </p>
          <p class="lm-note">
            Coming back later? Verify your email again to recover management
            access. Never send us your Google password. For help or withdrawal
            by email:
            <a href="mailto:lodomelix@ari.company">lodomelix@ari.company</a>.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
const requestId = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (x) => x.toString(16).padStart(2, "0")).join("");
};
export default {
  data: () => ({
    reviewDrafts: process.env.VUE_APP_INCLUDE_REVIEW_DRAFTS === "true",
    available: false,
    loading: true,
    minimumAge: 18,
    community: null,
    versions: {},
    started: Date.now(),
    reference: "",
    busy: false,
    code: "",
    state: "",
    stateMessage: "",
    optIn: "",
    emailNotice: "",
    verifiedEmail: "",
    authenticated: false,
    removalPending: false,
    confirmWithdraw: false,
    manageError: "",
    requestMessage: "",
    requestError: false,
    ageMessage: "",
    form: {
      email: "",
      age: "",
      display_name: "",
      device: "",
      age_confirmed: false,
      terms_accepted: false,
      community_requested: false,
      website: "",
    },
  }),
  computed: {
    stateTitle() {
      return {
        pending: "Email verified · Access pending",
        failed: "Access not confirmed",
        granted: "Access granted · Play opt-in required",
        withdrawn: "Registration withdrawn",
        awaiting_verification: "Awaiting email verification",
      }[this.state];
    },
  },
  async mounted() {
    this.reference = requestId();
    // Enrollment cannot open while its notices are excluded from the public build.
    if (!this.reviewDrafts) { this.loading = false; return; }
    try {
      const r = await fetch("/api/lodomelix/testing/status", {
        cache: "no-store",
      });
      if (!r.ok) throw Error();
      const s = await r.json();
      this.available = s.available === true;
      this.minimumAge = Math.max(18, s.minimum_age || 18);
      this.community = s.community;
      this.versions = {
        terms_version: s.terms_version,
        privacy_version: s.privacy_version,
      };
      if (this.available) {
        const r = await this.api("manage", {});
        if (r.ok) {
          this.authenticated = true;
          this.setState(r.data);
        }
      }
    } catch (e) {
      this.available = false;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async api(action, data) {
      if (!this.reviewDrafts) return { ok: false, data: { message: "Enrollment is not open yet." } };
      try {
        const r = await fetch("/api/lodomelix/testing/" + action, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        return { ok: r.ok, data: await r.json() };
      } catch (e) {
        return {
          ok: false,
          data: {
            message:
              "The connection was interrupted. Delivery or access is unconfirmed. Check your email and retry the access check before sending another request.",
          },
        };
      }
    },
    setState(data) {
      this.state = data.state;
      this.stateMessage = data.message;
      this.optIn = data.opt_in_url || "";
      this.emailNotice = data.email_notice || "";
      this.removalPending = data.removal_pending === true;
    },
    async register() {
      if (!Number.isInteger(this.form.age) || this.form.age < this.minimumAge) {
        this.ageMessage =
          "Registration is for adults aged " +
          this.minimumAge +
          " or over. Nothing was sent.";
        return;
      }
      this.busy = true;
      this.requestError = false;
      const r = await this.api("request", {
        ...this.form,
        ...this.versions,
        community_version: this.community && this.community.version,
        request_id: this.reference,
        elapsed_ms: Date.now() - this.started,
      });
      this.requestMessage = r.data.message;
      this.requestError = !r.ok;
      if (r.ok) {
        this.state = "awaiting_verification";
        this.stateMessage = r.data.message;
        this.verifiedEmail = this.form.email;
        this.reference = requestId();
        this.$nextTick(() => document.getElementById("lm-test-code").focus());
      }
      this.busy = false;
    },
    async verify() {
      this.busy = true;
      this.manageError = "";
      const r = await this.api("verify", { code: this.code });
      this.busy = false;
      if (!r.ok) {
        this.manageError = r.data.message;
        return;
      }
      this.code = "";
      this.requestMessage = "";
      this.authenticated = true;
      this.setState(r.data);
      if (this.state !== "withdrawn") await this.provision();
    },
    async provision() {
      this.busy = true;
      this.manageError = "";
      const r = await this.api("provision", {});
      if (r.data.state) this.setState(r.data);
      if (!r.ok) this.manageError = r.data.message;
      this.busy = false;
    },
    async withdraw() {
      this.busy = true;
      this.manageError = "";
      const r = await this.api("withdraw", {});
      if (r.ok) {
        this.setState(r.data);
        this.confirmWithdraw = false;
      } else this.manageError = r.data.message;
      this.busy = false;
    },
  },
};
</script>
